import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Card from '../card/card.component';
import CheckAccess from '../check-access/check-access.component';
import Tooltip from '../tooltip/tooltip.component';
import Loader from '../loader/loader.component';
import TextBlock from '../text-block/text-block.component';
import DeleteIcon from '../../icons/delete-icon';
import StructureService from '../../services/structure-service';
import FormatService from '../../services/format-service';
import useCurrentUserPermissions from '../../hooks/use-current-user-permissions';
import useScreenLarge from '../../hooks/use-screen-large';
import useScreenMedium from '../../hooks/use-screen-medium';
import useScreenCustom from '../../hooks/use-screen-custom';
import useStylesPageTabs from './page-tabs.styles';
import { TAB_INDEX_DEFAULT } from '../../constants/system/tab-indexes';

const PageTabs: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLOR_SECONDARY: ThemeColor = 'secondary';
  const COLOR_PRIMARY: ThemeColor = 'primary';
  const VARIANT_FULL_WIDTH: TabsVariant = 'fullWidth';
  const VARIANT_SCROLLABLE: TabsVariant = 'scrollable';
  const WIDTH_RESPONSIVE_STATE_1: number = 479;
  const WIDTH_RESPONSIVE_STATE_2: number = 599;
  const WIDTH_RESPONSIVE_STATE_3: number = 739;
  const WIDTH_RESPONSIVE_STATE_4: number = 879;
  const WIDTH_RESPONSIVE_STATE_5: number = 959;
  const WIDTH_RESPONSIVE_STATE_6: number = 1279;
  const WIDTH_RESPONSIVE_STATE_7: number = 1539;

  const {
    isSubTabs,
    isEditable,
    isDeletable,
    isCompressed,
    isDisabled,
    isLoading,
    withTabTopOffset,
    withNoTabSwitcher,
    tabs,
    variant,
    deletePermission,
    currentTab,
    onTabChange,
    onClick,
    onSetTabIndex,
    onBeforeTabChange,
    onDeleteClick,
    subComponent,
    staticComponent,
  } = props;

  const { classes } = useStylesPageTabs();
  const permissionList: string[] = useCurrentUserPermissions();
  const isLargeScreen: boolean = useScreenLarge();
  const isMediumScreen: boolean = useScreenMedium();
  const isResponsiveState1: boolean = useScreenCustom(WIDTH_RESPONSIVE_STATE_1);
  const isResponsiveState2: boolean = useScreenCustom(WIDTH_RESPONSIVE_STATE_2, WIDTH_RESPONSIVE_STATE_1 + 1);
  const isResponsiveState3: boolean = useScreenCustom(WIDTH_RESPONSIVE_STATE_3, WIDTH_RESPONSIVE_STATE_2 + 1);
  const isResponsiveState4: boolean = useScreenCustom(WIDTH_RESPONSIVE_STATE_4, WIDTH_RESPONSIVE_STATE_3 + 1);
  const isResponsiveState5: boolean = useScreenCustom(WIDTH_RESPONSIVE_STATE_5, WIDTH_RESPONSIVE_STATE_4 + 1);
  const isResponsiveState6: boolean = useScreenCustom(WIDTH_RESPONSIVE_STATE_6, WIDTH_RESPONSIVE_STATE_5 + 1);
  const isResponsiveState7: boolean = useScreenCustom(WIDTH_RESPONSIVE_STATE_7, WIDTH_RESPONSIVE_STATE_6 + 1);

  const isFullWidth: boolean = (!isLargeScreen && isCompressed) || (!isMediumScreen && !isCompressed);
  const isDeleteAllowed: boolean = deletePermission
    ? StructureService.checkPermission(deletePermission, permissionList) : true;

  const [currentTabIndex, setCurrentTabIndex]: [number | null, Function] = useState(null);

  const tabIconClassName: string | undefined = isDeletable && isDeleteAllowed
    ? FormatService.formatClassNames(classes.pageTabsTabIcon, classes.pageTabsTabIconResponsive) : undefined;
  const tabDeletableClassName: string | undefined = isDeletable && isDeleteAllowed && !isEditable
    ? FormatService.formatClassNames(classes.pageTabsTabDeletable, classes.pageTabsTabDeletableResponsive) : undefined;
  const withDisableProcessing: boolean = !!onBeforeTabChange;
  const tabDisabledClassName: string | undefined = isDisabled && !withDisableProcessing
    ? classes.pageTabsTabDisabled : undefined;

  const allowedTabs: TabItem[] = useMemo((): TabItem[] => (
    tabs && tabs.length ? tabs.filter((tabItem: TabItem): boolean => !tabItem.isHidden)
      .filter((tabItem: TabItem): boolean => (
        !tabItem.permission || (!!tabItem.permission
          && StructureService.checkPermission(tabItem.permission, permissionList))
      )) : []
  ), [tabs]);

  const allowedTabsTotal: number = allowedTabs && allowedTabs.length;

  let containerResponsiveClassName: string | undefined;
  if (isResponsiveState1) {
    containerResponsiveClassName = classes.containerPageTabsResponsiveState1;
  } else if (isResponsiveState2) {
    containerResponsiveClassName = classes.containerPageTabsResponsiveState2;
  } else if (isResponsiveState3) {
    containerResponsiveClassName = classes.containerPageTabsResponsiveState3;
  } else if (isResponsiveState4) {
    containerResponsiveClassName = classes.containerPageTabsResponsiveState4;
  } else if (isResponsiveState5) {
    containerResponsiveClassName = classes.containerPageTabsResponsiveState5;
  } else if (isResponsiveState6) {
    containerResponsiveClassName = classes.containerPageTabsResponsiveState6;
  } else if (isResponsiveState7 && isCompressed) {
    containerResponsiveClassName = classes.containerPageTabsResponsiveState7;
  }

  const tabChange: TabChangeHandlerFunction = useCallback((
    event: React.SyntheticEvent<Element, Event>,
    newTabIndex: number,
  ): void => {
    if (onBeforeTabChange) {
      onBeforeTabChange(newTabIndex, isSubTabs);
    }

    if (isDisabled) {
      return;
    }

    setCurrentTabIndex(newTabIndex);

    let allowedTabId: number | null = null;
    allowedTabs.forEach((tabItem: TabItem, tabIndex: number): void => {
      if (tabIndex === newTabIndex) {
        allowedTabId = tabItem.id;
      }
    });

    if (onTabChange) {
      onTabChange(allowedTabId);
    }
  }, [isDisabled, allowedTabs, isSubTabs]);

  const tabClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, tabId: number, tabIndex: number,
  ): void => {
    if (onClick && (!isDisabled || withDisableProcessing)) {
      onClick(event, tabId);
    }

    if (isEditable && (currentTabIndex === tabIndex)) {
      event.preventDefault();
    }
  }, [isDisabled, isEditable, currentTabIndex, withDisableProcessing]);

  useEffect((): void => {
    if (allowedTabs) {
      allowedTabs.forEach((tabItem: TabItem, tabIndex: number): void => {
        if (((currentTab && tabItem.name === currentTab) || (!currentTab && tabIndex === TAB_INDEX_DEFAULT))
          && ((!currentTabIndex && currentTabIndex !== TAB_INDEX_DEFAULT) || (currentTabIndex !== tabIndex))) {
          setCurrentTabIndex(tabIndex);
        }
      });
    }
  }, [allowedTabsTotal, currentTab]);

  useEffect((): void => {
    if (onSetTabIndex) {
      onSetTabIndex(currentTabIndex);
    }
  }, [currentTabIndex]);

  return (
    <div className={classes.pageTabs}>
      {
        !withNoTabSwitcher && (
          <Card>
            {
              isFullWidth && (currentTabIndex || currentTabIndex === TAB_INDEX_DEFAULT) && (
                <Tabs
                  value={currentTabIndex}
                  indicatorColor={COLOR_SECONDARY}
                  textColor={COLOR_PRIMARY}
                  variant={variant || VARIANT_FULL_WIDTH}
                  className={classes.pageTabsTabs}
                  onChange={tabChange}
                >
                  {
                    allowedTabs && !!allowedTabs.length
                    && allowedTabs.map((tabItem: TabItem, tabIndex: number): ReactElement => (
                      <Tab
                        disableRipple={(isDisabled && !withDisableProcessing) || isDeletable
                          || (isEditable && currentTabIndex === tabIndex)}
                        key={tabItem.id}
                        value={tabIndex}
                        className={FormatService.formatClassNames(
                          classes.pageTabsTab, tabIconClassName, tabDisabledClassName,
                          currentTabIndex === tabIndex ? classes.pageTabsTabActive : undefined,
                          currentTabIndex !== tabIndex ? tabDeletableClassName : undefined,
                        )}
                        onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                          tabClick(event, tabItem.id, tabIndex);
                        }}
                        label={(
                          tabItem.tabComponent || (
                            <TextBlock>
                              {tabItem.title}
                            </TextBlock>
                          )
                        )}
                        icon={(
                          isDeletable && isDeleteAllowed && !isEditable && currentTabIndex !== tabIndex ? (
                            <CheckAccess permissions={deletePermission ? [deletePermission] : []}>
                              <Tooltip title="Delete">
                                <DeleteIcon
                                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                    onDeleteClick(event, tabItem.id);
                                  }}
                                />
                              </Tooltip>
                            </CheckAccess>
                          ) : undefined
                        )}
                      />
                    ))
                  }
                </Tabs>
              )
            }
            {
              !isFullWidth && (currentTabIndex || currentTabIndex === TAB_INDEX_DEFAULT) && (
                <Tabs
                  scrollButtons
                  allowScrollButtonsMobile
                  indicatorColor={COLOR_SECONDARY}
                  textColor={COLOR_PRIMARY}
                  variant={VARIANT_SCROLLABLE}
                  value={currentTabIndex}
                  classes={{
                    root: classes.pageTabsTabs,
                    flexContainer: FormatService.formatClassNames(
                      classes.containerPageTabsResponsive, containerResponsiveClassName,
                    ),
                  }}
                  onChange={tabChange}
                >
                  {
                    allowedTabs && !!allowedTabs.length
                    && allowedTabs.map((tabItem: TabItem, tabIndex: number): ReactElement => (
                      <Tab
                        disableRipple={(isDisabled && !withDisableProcessing) || isDeletable
                          || (isEditable && currentTabIndex === tabIndex)}
                        key={tabItem.id}
                        label={tabItem.title}
                        value={tabIndex}
                        className={FormatService.formatClassNames(
                          classes.pageTabsTab, tabIconClassName, tabDisabledClassName,
                          currentTabIndex === tabIndex ? classes.pageTabsTabActive : undefined,
                          currentTabIndex !== tabIndex && !isEditable ? tabDeletableClassName : undefined,
                        )}
                        onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                          tabClick(event, tabItem.id, tabIndex);
                        }}
                        icon={(
                          isDeletable && isDeleteAllowed && !isEditable && currentTabIndex !== tabIndex ? (
                            <CheckAccess permissions={deletePermission ? [deletePermission] : []}>
                              <Tooltip title="Delete">
                                <DeleteIcon
                                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                    onDeleteClick(event, tabItem.id);
                                  }}
                                />
                              </Tooltip>
                            </CheckAccess>
                          ) : undefined
                        )}
                      />
                    ))
                  }
                </Tabs>
              )
            }
          </Card>
        )
      }
      <div className={classes.pageTabsContent}>
        {
          isLoading && (
            <Loader isPageLoader withTopOffset={withTabTopOffset} className={classes.pageTabsLoader} />
          )
        }
        {
          !isLoading && subComponent
        }
        {
          !isLoading && (
            staticComponent ? (
              <div>
                {staticComponent}
              </div>
            ) : (
              allowedTabs && !!allowedTabs.length
              && allowedTabs.map((tabItem: TabItem, tabIndex: number): ReactElement => (
                <div key={tabItem.id} className={currentTabIndex !== tabIndex ? classes.pageTabsTabHidden : undefined}>
                  {tabItem.component}
                </div>
              ))
            )
          )
        }
      </div>
    </div>
  );
});

export default PageTabs;
